import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import {
  Addon,
  AddonsList,
  MasterPackage,
  MasterProduct,
  NewOrderInitiationProps,
  OrderServiceProps,
  NewOrderSelectedProduct,
  //SelectedService,
  // PackagingDetail,
  SpeciesItem,
  ProductDraft,
  SpeciesSet,
  ProductTypeInfo,
  SpeciesStatus,
} from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import { Axios } from '@helpers/ApiService';
import {
  OrderCreationMasterData,
  errorMessage,
  CURRENT_CUSTOMIZING_ID,
  ProductType,
  responseTexts,
} from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { useToast } from '@contexts/Toast';
import { CopyProductModal } from '@components/Modal/NewOrder/CopyProductModal';
import { EditProductModal } from '@components/Modal/NewOrder/EditProductModal';
import { ConvertToStorageFormat } from '@helpers/ConverToStorageFormat';

// const statusConfig = {
//   completed: { color: '#1BB700', label: 'Completed' },
//   missing: { color: '#FFB800', label: 'Missing Details' },
//   not_started: { color: '#D9D9D9', label: 'Not Started' },
//   editing: { color: '#2A6FBA', label: 'Edited' },
// };
const statusConfig = {
  completed: { color: '#1BB700', label: 'Completed' },
  missing: { color: '#FFB800', label: 'Not Saved' },
  not_started: { color: '#D9D9D9', label: 'Not Started' },
  editing: { color: '#2A6FBA', label: 'Editing' },
  edited: { color: '#2A6FBA', label: 'Edited' },
};

const normalizePackagePercentage = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(100, Math.round(parsed)));
};

const OrderSplitView = ({
  selectedServiceIds,
  addedProducts,
  handleEditProduct,
  handleDeleteProduct,
  handleDuplicateProduct,
  onUpdateCompletedSet,
  handleDeleteService,
  //onUpdateCompletedSetServices,
  isEditOrder,
}: {
  selectedServiceIds: number[];
  addedProducts: ProductDraft[];
  handleEditProduct: (
    updatedProduct: ProductDraft,
    index: number,
    setIndex: number,
  ) => void;
  handleDeleteProduct: (
    index: number,
    setIndex: number,
    butcherProductId?: number | null,
  ) => void;
  handleDuplicateProduct: (
    updatedProduct: ProductDraft,
    setIndex: number,
  ) => void;
  onUpdateCompletedSet: (
    setIndex: number,
    updatedProducts: NewOrderSelectedProduct[],
  ) => void;
  handleDeleteService: (serviceId: number, setIndex: number) => void;
  // onUpdateCompletedSetServices: (
  //   setIndex: number,
  //   updatedServices: SelectedService[],
  // ) => void;
  isEditOrder: boolean;
}) => {
  const { showConfirmationToast } = UseConfirmationToast();
  const { showToastMessage } = useToast();

  const [splitViewServiceOpen, setSplitViewServiceOpen] = useState(false);
  const [splitViewProductOpen, setSplitViewProductOpen] = useState(false);

  const [editProductModal, setEditProductModal] = useState(false);
  const [copyProductModal, setCopyProductModal] = useState(false);

  const [services, setServices] = useState<OrderServiceProps[]>([]);
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();

  const [addons, setAddons] = useState<AddonsList[]>([]);
  const [masterPackages, setMasterPackages] = useState<MasterPackage[]>([]);

  const [speciesSets, setSpeciesSets] = useState<SpeciesSet[]>([]);
  const currentCustomizingId = Number(
    localStorage.getItem(CURRENT_CUSTOMIZING_ID),
  );

  const [openSets, setOpenSets] = useState<Record<number, boolean>>({
    [currentCustomizingId]: true,
  });

  const [selectedProduct, setSelectedProduct] = useState<ProductDraft | null>(
    null,
  );
  const [isDuplicate, setIsDuplicate] = useState(false);

  const [selectedProductIndex, setSelectedProductIndex] = useState<
    number | null
  >(null);
  const [editingCompletedSetIndex, setEditingCompletedSetIndex] = useState<
    number | null
  >(null);
  const [editingCompletedSetData, setEditingCompletedSetData] =
    useState<SpeciesItem | null>(null);
  const [targetSetIndex, setTargetSetIndex] = useState<number | null>(null);
  const [productType, setProductType] = useState<{
    productType: number;
    minValue: number;
  }>({
    productType: 1,
    minValue: 1,
  });

  /**
   * Fetches customer details when the component mounts
   * and stores them in the local state.
   *
   * This runs only once because the dependency array is empty.
   * Customer data is retrieved from CustomerOrderService.
   */
  useEffect(() => {
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);
  }, []);

  /**
   * Loads master data required for species customization.
   *
   * When customerDetails changes, the order data is retrieved.
   * If a speciesId exists in the order, the following master data is fetched:
   * - Services related to the selected species
   * - Master product list
   * - Addons list
   * - Master packages
   *
   * This ensures all required data is available for the customization screen.
   */
  useEffect(() => {
    const customerOrder = getOrderData();
    if (customerOrder?.speciesId) {
      fetchServiceMaster(customerOrder.speciesId);
      fetchMasterProducts();
      fetchAddons();
      fetchMasterPackages();
    }
  }, [customerDetails]);

  useEffect(() => {
    if (isEditOrder) return;

    const storageKey = 'orderData';
    const raw = localStorage.getItem(storageKey);
    const all = raw ? JSON.parse(raw) : null;
    const customerId = Number(customerDetails?.id);
    const savedSet = all?.[customerId]?.species?.[currentCustomizingId];

    const savedServiceIds = ((savedSet?.services as Array<{
      serviceId?: number;
      butcherServiceId?: number;
    }>) ?? [])
      .map((service) => Number(service.serviceId ?? service.butcherServiceId ?? 0))
      .filter((id) => id > 0)
      .sort((a, b) => a - b);

    const liveServiceIds = [...selectedServiceIds].sort((a, b) => a - b);

    const savedProductKeys = ((savedSet?.products as Array<{
      productId?: number;
      butcherProductId?: number;
      quantity?: number;
      fatPercent?: number;
      addOnIds?: Array<number | string>;
    }>) ?? [])
      .map((product) => {
        const addOnIds = (product.addOnIds ?? [])
          .map((id) => Number(id))
          .sort((a, b) => a - b)
          .join(',');
        return [
          Number(product.productId ?? product.butcherProductId ?? 0),
          Number(product.quantity ?? 0),
          Number(product.fatPercent ?? 0),
          addOnIds,
        ].join('|');
      })
      .sort();

    const liveProductKeys = addedProducts
      .map((product) => {
        const addOnIds = (product.addons ?? [])
          .map((addon) => Number(addon.id))
          .sort((a, b) => a - b)
          .join(',');
        return [
          Number(product.productId ?? product.butcherProductId ?? 0),
          Number(product.quantity ?? 0),
          Number(product.fatPercentage ?? 0),
          addOnIds,
        ].join('|');
      })
      .sort();

    const hasSavedData =
      (savedSet?.services?.length ?? 0) > 0 || (savedSet?.products?.length ?? 0) > 0;

    const isDirty =
      JSON.stringify(savedServiceIds) !== JSON.stringify(liveServiceIds) ||
      JSON.stringify(savedProductKeys) !== JSON.stringify(liveProductKeys);

    setSpeciesSets((prevSets) =>
      prevSets.map((set) => {
        if (set.setIndex !== currentCustomizingId) return set;

        return {
          ...set,
          status: isDirty ? 'missing' : hasSavedData ? 'completed' : 'missing',
        };
      }),
    );
  }, [
    selectedServiceIds,
    addedProducts,
    currentCustomizingId,
    customerDetails?.id,
    isEditOrder,
  ]);

  useEffect(() => {
    const handleOrderDataUpdated = () => {
      if (customerDetails?.id) {
        getOrderData();
      }
    };

    window.addEventListener('order-data-updated', handleOrderDataUpdated);

    return () => {
      window.removeEventListener('order-data-updated', handleOrderDataUpdated);
    };
  }, [customerDetails]);

  /**
   * Fetches the service master data for a given species.
   * The retrieved services are stored in the services state.
   *
   * @param speciesId - The ID of the species whose service master data needs to be fetched.
   * @returns Promise<void>
   */
  const fetchServiceMaster = async (speciesId: number): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        speciesId: speciesId,
        masterDataType: OrderCreationMasterData.SERVICE_MASTER_DATA,
      });
      setServices(response.data.data.masterData.services);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the master list of products available for order creation
   * and stores them in the masterProducts state.
   *
   * @returns Promise<void>
   */
  const fetchMasterProducts = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PRODUCT_MASTER_DATA,
      });
      setMasterProducts(response.data.data.masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches all available addons and filters only those marked as available.
   * Only addons with availableFlag = 1 are stored in the addons state.
   *
   * @returns Promise<void>
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      //filter through the addons and set only the addons where availableFlag is 1
      const availableAddons = response.data.data.addOns.filter(
        (addon: Addon) => addon.availableFlag === 1,
      );
      setAddons(availableAddons);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the master list of package types used for product packaging
   * and stores them in the masterPackages state.
   *
   * @returns Promise<void>
   */
  const fetchMasterPackages = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      const masterData = response.data.data.masterData.packageType;
      setMasterPackages(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Toggles the open/closed state of a set based on its index.
   *
   * Updates the openSets state by flipping the boolean value
   * for the given index while preserving the existing state.
   *
   * @param index - The index of the set whose visibility should be toggled.
   */
  const toggleSet = (index: number) => {
    setOpenSets((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  /**
   * Retrieves the order data for the current customer from localStorage and updates the relevant state.
   *
   * - Fetches the `orderData` object from localStorage.
   * - Parses the data and extracts the order specific to the current `customerId`.
   * - Sets both `quantity` and `orderData` state values using the retrieved data.
   */
  const getOrderData = () => {
    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    const parsedOrderData = JSON.parse(raw);
    if (!parsedOrderData) return;

    const customerOrder = parsedOrderData[Number(customerDetails?.id)];
    if (!customerOrder) return;

    const { quantity, speciesName, species } = customerOrder;
    const currentCustomizingId = Number(
      localStorage.getItem(CURRENT_CUSTOMIZING_ID),
    );

    const sets: SpeciesSet[] = Array.from(
      { length: Number(quantity) },
      (_, i) => {
        const setData = species?.[i];
        const hasData =
          setData &&
          (setData.products?.length > 0 || setData.services?.length > 0);
        const isEditing =
          Number(localStorage.getItem(CURRENT_CUSTOMIZING_ID)) === i;
        let status;
        if (isEditOrder) {
          status = setData?.isEdited ? 'edited' : null;
        } else {
          status = hasData
            ? 'completed'
            : isEditing
              ? 'missing'
              : 'not_started';
        }
        return {
          setIndex: i,
          speciesName,
          status: status as SpeciesStatus,
          data: setData ?? null,
        };
      },
    );
    setSpeciesSets(sets);
    setOpenSets({ [currentCustomizingId]: true });
    setSplitViewServiceOpen(true);
    setSplitViewProductOpen(true);

    return customerOrder;
  };

  /**
   * Retrieves and hydrates product drafts for a specific species set index.
   * * This function:
   * 1. Locates the correct order draft in localStorage based on the current mode (Edit/Create).
   * 2. Accesses the specific species data using the provided `setIndex`.
   * 3. Maps raw product data back into `ProductDraft` objects by cross-referencing:
   * - Master Products (for names and prices)
   * - Master Addons (for available flags and addon details)
   * - Master Packages (for packaging units and sizes)
   * * @param {number} setIndex - The index of the species/item set within the customer's order.
   * @returns {ProductDraft[]} An array of fully populated product draft objects.
   */
  const getProductsForSet = (setIndex: number): ProductDraft[] => {
    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];

    const all = JSON.parse(raw);
    const customerId = String(customerDetails?.id);
    const speciesData = all[customerId]?.species?.[setIndex];
    if (!speciesData?.products) return [];

    return speciesData.products.map((product: any) => {
      const masterProduct = masterProducts.find(
        (mp) => mp.id === product.butcherProductId,
      );
      return {
        productId: product.productId,
        butcherProductId: product.butcherProductId,
        name: masterProduct?.name ?? '',
        price: masterProduct?.price ?? 0,
        fatPercentage: String(product.fatPercent),
        quantity: product.quantity,
        instruction: product.packagingNotes ?? '',
        addons:
          product.addOnIds?.map((addonId: number) => {
            const masterAddon = addons.find((a) => a.id === addonId);
            return {
              id: addonId,
              name: masterAddon?.name ?? '',
              availableFlag: masterAddon?.availableFlag ?? false,
              price: masterAddon?.price ?? 0,
            };
          }) ?? [],
        packages:
          product.packaging?.map((pkg: any) => {
            const masterPkg = masterPackages.find(
              (mp) => mp.id === pkg.productPackageTypeId,
            );
            return {
              id: pkg.productPackageTypeId,
              packageType: masterPkg?.packageType ?? '',
              packageSize: masterPkg?.packageSize ?? 0,
              unit: masterPkg?.unit ?? '',
              percentage: normalizePackagePercentage(pkg.percentage),
            };
          }) ?? [],
        notes: product.packagingNotes ?? '',
        allowFatContentFlag: masterProduct?.allowFatContentFlag ?? 0,
        allowAddOnFlag: masterProduct?.allowAddOnFlag ?? 0,
        upchargeIngredientsFlag: masterProduct?.upchargeIngredientsFlag ?? 0,
      };
    });
  };

  /**
   * Opens the edit modal for the selected product.
   *
   * Sets the selected product data, marks the action as an edit
   * (not a duplicate), and stores the index of the product being edited.
   *
   * @param product - The product draft selected for editing.
   * @param index - The index of the product in the product list.
   */
  const handleEditClick = (
    product: ProductDraft,
    index: number,
    productType: ProductTypeInfo,
    setIndex: number,
  ) => {
    setSelectedProduct(product);
    setIsDuplicate(false);
    setProductType(productType);
    setEditProductModal(true);
    setSelectedProductIndex(index);
    setTargetSetIndex(setIndex);
  };

  /**
   * Opens the duplicate product modal.
   *
   * Sets the selected product data and marks the action as a duplicate
   * so a copy of the product can be created.
   *
   * @param product - The product draft selected for duplication.
   */
  const handleDuplicateClick = (
    product: ProductDraft,
    productType: ProductTypeInfo,
    setIndex: number,
  ) => {
    setSelectedProduct(product);
    setProductType(productType);
    setIsDuplicate(true);
    setCopyProductModal(true);
    setTargetSetIndex(setIndex);
  };

  /**
   * Handles saving a product from the edit or duplicate modal.
   *
   * Behavior:
   * - If editing a product inside a completed set:
   *   - Converts the updated product to storage format.
   *   - Updates or duplicates the product within the completed set.
   *   - Calls onUpdateCompletedSet to persist the changes.
   *
   * - If editing outside a completed set:
   *   - Updates the existing product or creates a duplicate
   *     depending on the isDuplicate flag.
   *
   * After saving, all modal and editing states are reset.
   *
   * @param updatedProduct - The updated product draft returned from the modal form.
   */
  const handleModalSave = (updatedProduct: ProductDraft) => {
    if (editingCompletedSetIndex !== null && editingCompletedSetData) {
      const currentProducts = editingCompletedSetData.products ?? [];
      const convertedProduct = ConvertToStorageFormat(updatedProduct);
      if (convertedProduct.productId === null) return;
      const updatedProducts: NewOrderSelectedProduct[] = isDuplicate
        ? [...currentProducts, convertedProduct as NewOrderSelectedProduct]
        : currentProducts.map((p: NewOrderSelectedProduct, i: number) =>
          i === selectedProductIndex
            ? (convertedProduct as NewOrderSelectedProduct)
            : p,
        );
      onUpdateCompletedSet(editingCompletedSetIndex, updatedProducts);
      getOrderData();
    } else {
      if (isDuplicate) {
        handleDuplicateProduct(updatedProduct, targetSetIndex!);
      } else {
        handleEditProduct(
          updatedProduct,
          selectedProductIndex!,
          targetSetIndex!,
        );
      }
    }
    setEditProductModal(false);
    setCopyProductModal(false);
    setSelectedProduct(null);
    setSelectedProductIndex(null);
    setEditingCompletedSetIndex(null);
    setEditingCompletedSetData(null);
    setIsDuplicate(false);
  };

  return (
    <>
      {/* Spilt view  */}
      <div className="col-span-2 bg-[#E0EBEA] p-2 rounded-sm sticky top-2 h-screen overflow-y-auto">
        <div className="text-[#49494C] flex items-center gap-2">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.49563 16C4.08188 16 3.72917 15.8527 3.4375 15.5581C3.14583 15.2635 3 14.9094 3 14.4956C3 14.0819 3.14729 13.7292 3.44188 13.4375C3.73646 13.1458 4.09063 13 4.50438 13C4.91813 13 5.27083 13.1473 5.5625 13.4419C5.85417 13.7365 6 14.0906 6 14.5044C6 14.9181 5.85271 15.2708 5.55813 15.5625C5.26354 15.8542 4.90938 16 4.49563 16ZM13.4956 16C13.0819 16 12.7292 15.8527 12.4375 15.5581C12.1458 15.2635 12 14.9094 12 14.4956C12 14.0819 12.1473 13.7292 12.4419 13.4375C12.7365 13.1458 13.0906 13 13.5044 13C13.9181 13 14.2708 13.1473 14.5625 13.4419C14.8542 13.7365 15 14.0906 15 14.5044C15 14.9181 14.8527 15.2708 14.5581 15.5625C14.2635 15.8542 13.9094 16 13.4956 16ZM4.27083 3.5L6 7.5H12.2708L13.9792 3.5H4.27083ZM3.625 2H15.5C15.6944 2 15.8368 2.07639 15.9271 2.22917C16.0174 2.38194 16.0278 2.54167 15.9583 2.70833L13.6381 8.08042C13.5183 8.36014 13.3368 8.58333 13.0938 8.75C12.8507 8.91667 12.5764 9 12.2708 9H5.60417L4.72917 10.5H15V12H4.75C4.15278 12 3.71181 11.749 3.42708 11.2469C3.14236 10.7447 3.14583 10.2457 3.4375 9.75L4.52083 7.875L1.79167 1.5H0V0H2.77083L3.625 2Z"
              fill="#49494C"
            />
          </svg>
          <span className="text-[18px]">Cart</span>
        </div>

        {speciesSets.map((set) => {
          const isOpen = openSets[set.setIndex] ?? false;
          const { color, label } = statusConfig[set.status] || {};
          return (
            <div
              key={set.setIndex}
              className="mt-2 rounded-md bg-white overflow-hidden"
            >
              {/* Accordion Header */}
              <button
                type="button"
                onClick={() => toggleSet(set.setIndex)}
                className="w-full p-2 flex items-center justify-between cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#49494C]">
                    {set.speciesName} - {set.setIndex + 1}
                  </span>
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <circle cx="4" cy="4" r="4" fill={color} />
                  </svg>
                  <span className="text-sm" style={{ color }}>
                    {label}
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={faChevronUp}
                  className={`text-[#49494C] transition-transform duration-300 ${isOpen ? '' : 'rotate-180'}`}
                />
              </button>

              {/* Accordion Content */}
              {isOpen && (
                <>
                  {/* Services */}
                  <div className="px-3 pb-3 pt-1">
                    <div className="rounded-md overflow-hidden border border-[#EEEEEE]">
                      <button
                        onClick={() =>
                          setSplitViewServiceOpen(!splitViewServiceOpen)
                        }
                        type="button"
                        className="w-full flex justify-between items-center bg-[#FFFEFA] p-2 font-semibold text-lg outline-none"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F3E8FF]">
                            <svg
                              width="20"
                              height="16"
                              viewBox="0 0 20 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M0 16V6C0 5.45 0.195833 4.97917 0.5875 4.5875C0.979167 4.19583 1.45 4 2 4H5V2C5 1.45 5.19583 0.979167 5.5875 0.5875C5.97917 0.195833 6.45 0 7 0H13C13.55 0 14.0208 0.195833 14.4125 0.5875C14.8042 0.979167 15 1.45 15 2V4H18C18.55 4 19.0208 4.19583 19.4125 4.5875C19.8042 4.97917 20 5.45 20 6V16H0ZM6 11V12H4V11H2V14H18V11H16V12H14V11H6ZM2 6V9H4V8H6V9H14V8H16V9H18V6H2ZM7 4H13V2H7V4Z"
                                fill="#9F57D4"
                              />
                            </svg>
                          </div>
                          <span>Services</span>
                        </div>
                        <FontAwesomeIcon
                          icon={faChevronUp}
                          className={`text-[#9F57D4] transition-transform duration-300 ${splitViewServiceOpen ? '' : 'rotate-180'}`}
                        />
                      </button>

                      {/* Services */}
                      {splitViewServiceOpen && (
                        <div className="bg-[#FFFEFA]">
                          {(() => {
                            const currentCustomizingId = Number(
                              localStorage.getItem(CURRENT_CUSTOMIZING_ID),
                            );
                            const serviceIds =
                              set.setIndex === currentCustomizingId
                                ? selectedServiceIds
                                : (() => {
                                  const storageKey = isEditOrder
                                    ? 'editOrderData'
                                    : 'orderData';
                                  const raw =
                                    localStorage.getItem(storageKey);
                                  if (!raw) return [];
                                  const all = JSON.parse(raw);
                                  const customerId = String(
                                    customerDetails?.id,
                                  );
                                  return (
                                    all[customerId]?.species?.[
                                      set.setIndex
                                    ]?.services?.map(
                                      (s: any) =>
                                        s.butcherServiceId ?? s.serviceId,
                                    ) ?? []
                                  );
                                })();

                            const filteredServices = services.filter((s) =>
                              serviceIds.includes(s.id),
                            );

                            if (filteredServices.length === 0) {
                              return (
                                <p className="p-3 text-sm text-[#9E9E9E]">
                                  No services added yet.
                                </p>
                              );
                            }

                            return filteredServices.map((service) => {
                              const storageKey = isEditOrder
                                ? 'editOrderData'
                                : 'orderData';
                              const raw = localStorage.getItem(storageKey);
                              const all = raw ? JSON.parse(raw) : {};
                              const customerId = String(customerDetails?.id);
                              const orderService = all[customerId]?.species?.[
                                set.setIndex
                              ]?.services?.find(
                                (s: any) =>
                                  (s.butcherServiceId ?? s.serviceId) ===
                                  service.id,
                              );
                              const isCompleted =
                                (orderService?.status ?? 0) > 0;

                              return (
                                <div key={service.id}>
                                  <div className="flex items-center justify-between p-2">
                                    <span
                                      className={` ${isCompleted ? 'text-[#49494C]' : 'text-[#1C1B1F]'} font-medium`}
                                    >
                                      {service.name}
                                    </span>
                                    <div className="flex items-center gap-2">
                                      {isCompleted ? (
                                        <div className="flex items-center gap-1 bg-badge-green-bg text-badge-green border border-badge-green text-xs font-semibold px-2 py-1 rounded-full">
                                          <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                          >
                                            <circle
                                              cx="6"
                                              cy="6"
                                              r="6"
                                              fill="#16a34a"
                                            />
                                            <path
                                              d="M3 6l2 2 4-4"
                                              stroke="white"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                          Completed
                                        </div>
                                      ) : (
                                        <FontAwesomeIcon
                                          icon={faTrashCan}
                                          className="text-[#4D545C] border p-1 rounded-md cursor-pointer hover:opacity-70"
                                          onClick={() =>
                                            showConfirmationToast(
                                              'Are you sure you want to delete this service?',
                                              'Delete Service',
                                              'delete',
                                              () =>
                                                handleDeleteService(
                                                  service.id,
                                                  set.setIndex,
                                                ),
                                              'Delete',
                                              undefined,
                                              'Cancel',
                                            )
                                          }
                                        />
                                      )}
                                    </div>
                                  </div>
                                  <div className="border border-b my-1"></div>
                                </div>
                              );
                            });
                          })()}
                          {/* <div className="border-t border-[#EEEEEE]" /> */}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Products */}
                  <div className="px-3 pb-3 pt-1">
                    <div className="rounded-md overflow-hidden border border-[#EEEEEE]">
                      <button
                        onClick={() =>
                          setSplitViewProductOpen(!splitViewProductOpen)
                        }
                        type="button"
                        className="w-full flex justify-between items-center bg-[#FFFEFA] p-2 font-semibold text-lg outline-none"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F9E6DF]">
                            <svg
                              width="20"
                              height="16"
                              viewBox="0 0 21 18"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M19.0469 8.05V16C19.0469 16.55 18.8511 17.0208 18.4594 17.4125C18.0678 17.8042 17.5969 18 17.0469 18H3.04694C2.49694 18 2.02611 17.8042 1.63444 17.4125C1.24277 17.0208 1.04694 16.55 1.04694 16V8.05C0.663606 7.7 0.367772 7.25 0.159439 6.7C-0.0488946 6.15 -0.0530612 5.55 0.146939 4.9L1.19694 1.5C1.33027 1.06667 1.56777 0.708333 1.90944 0.425C2.25111 0.141667 2.64694 0 3.09694 0H16.9969C17.4469 0 17.8386 0.1375 18.1719 0.4125C18.5053 0.6875 18.7469 1.05 18.8969 1.5L19.9469 4.9C20.1469 5.55 20.1428 6.14167 19.9344 6.675C19.7261 7.20833 19.4303 7.66667 19.0469 8.05ZM12.2469 7C12.6969 7 13.0386 6.84583 13.2719 6.5375C13.5053 6.22917 13.5969 5.88333 13.5469 5.5L12.9969 2H11.0469V5.7C11.0469 6.05 11.1636 6.35417 11.3969 6.6125C11.6303 6.87083 11.9136 7 12.2469 7ZM7.74694 7C8.13027 7 8.44277 6.87083 8.68444 6.6125C8.92611 6.35417 9.04694 6.05 9.04694 5.7V2H7.09694L6.54694 5.5C6.48027 5.9 6.56777 6.25 6.80944 6.55C7.05111 6.85 7.36361 7 7.74694 7ZM3.29694 7C3.59694 7 3.85944 6.89167 4.08444 6.675C4.30944 6.45833 4.44694 6.18333 4.49694 5.85L5.04694 2H3.09694L2.09694 5.35C1.99694 5.68333 2.05111 6.04167 2.25944 6.425C2.46777 6.80833 2.81361 7 3.29694 7ZM16.7969 7C17.2803 7 17.6303 6.80833 17.8469 6.425C18.0636 6.04167 18.1136 5.68333 17.9969 5.35L16.9469 2H15.0469L15.5969 5.85C15.6469 6.18333 15.7844 6.45833 16.0094 6.675C16.2344 6.89167 16.4969 7 16.7969 7ZM3.04694 16H17.0469V8.95C16.9636 8.98333 16.9094 9 16.8844 9H16.7969C16.3469 9 15.9511 8.925 15.6094 8.775C15.2678 8.625 14.9303 8.38333 14.5969 8.05C14.2969 8.35 13.9553 8.58333 13.5719 8.75C13.1886 8.91667 12.7803 9 12.3469 9C11.8969 9 11.4761 8.91667 11.0844 8.75C10.6928 8.58333 10.3469 8.35 10.0469 8.05C9.76361 8.35 9.43444 8.58333 9.05944 8.75C8.68444 8.91667 8.28027 9 7.84694 9C7.36361 9 6.92611 8.91667 6.53444 8.75C6.14277 8.58333 5.79694 8.35 5.49694 8.05C5.14694 8.4 4.80111 8.64583 4.45944 8.7875C4.11777 8.92917 3.73027 9 3.29694 9H3.18444C3.14277 9 3.09694 8.98333 3.04694 8.95V16Z"
                                fill="#AD6749"
                              />
                            </svg>
                          </div>
                          <span>Products</span>
                        </div>
                        <FontAwesomeIcon
                          icon={faChevronUp}
                          className={`text-[#AD6749] transition-transform duration-300 ${splitViewProductOpen ? '' : 'rotate-180'}`}
                        />
                      </button>
                      {/* Products */}
                      {splitViewProductOpen && (
                        <div className="p-2 text-sm">
                          {(() => {
                            const currentCustomizingId = Number(
                              localStorage.getItem(CURRENT_CUSTOMIZING_ID),
                            );
                            const products =
                              set.setIndex === currentCustomizingId
                                ? addedProducts // use live state for currently active species
                                : getProductsForSet(set.setIndex); // use localStorage for others

                            if (products.length === 0) {
                              return (
                                <p className="p-2 text-sm text-[#9E9E9E]">
                                  No products added yet.
                                </p>
                              );
                            }

                            return products.map(
                              (addedProduct: ProductDraft, index: number) => {
                                const masterProduct = masterProducts.find(
                                  (p) => {
                                    if (isEditOrder) {
                                      // In Edit Mode, strictly match against the Master ID (butcherProductId)
                                      return (
                                        p.id === addedProduct.butcherProductId
                                      );
                                    }
                                    // In New Order Mode, match against the standard productId
                                    return p.id === addedProduct.productId;
                                  },
                                );
                                if (!masterProduct) return null;
                                const productInfo = {
                                  productType: masterProduct.productType,
                                  minValue:
                                    masterProduct.productType ===
                                      ProductType.QUANTITY
                                      ? masterProduct.minQuantity
                                      : masterProduct.weight,
                                  allowFatContentFlag:
                                    masterProduct.allowFatContentFlag,
                                  allowAddOnFlag:
                                    masterProduct.allowAddOnFlag,
                                  upchargeIngredientsFlag:
                                    masterProduct.upchargeIngredientsFlag,
                                };
                                return (
                                  <div
                                    key={index}
                                    className="border border-[#0000001A] rounded-md overflow-hidden mb-2"
                                  >
                                    <div className="bg-white p-3">
                                      <div className="bg-[#F9F3F1] p-2 flex items-center justify-between rounded-md">
                                        <span className="font-medium text-[#49494C]">
                                          {masterProduct?.name ??
                                            addedProduct.name}
                                        </span>
                                        {(addedProduct.status ?? 0) > 0 ? (
                                          <div className="flex items-center gap-1 bg-badge-green-bg text-badge-green border border-badge-green text-xs font-semibold px-2 py-1 rounded-full">
                                            <svg
                                              width="12"
                                              height="12"
                                              viewBox="0 0 12 12"
                                              fill="none"
                                            >
                                              <circle
                                                cx="6"
                                                cy="6"
                                                r="6"
                                                fill="#16a34a"
                                              />
                                              <path
                                                d="M3 6l2 2 4-4"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                            Completed
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              className="px-2 py-1 text-sm text-[#4B5563] bg-white rounded"
                                              onClick={() =>
                                                showConfirmationToast(
                                                  'Are you sure you want to edit product?',
                                                  'Edit Product',
                                                  'info',
                                                  () =>
                                                    handleEditClick(
                                                      addedProduct,
                                                      index,
                                                      productInfo,
                                                      set.setIndex,
                                                    ),
                                                  'Edit',
                                                  () => { },
                                                  'Cancel',
                                                )
                                              }
                                            >
                                              <svg
                                                width="12"
                                                height="14"
                                                viewBox="0 0 14 14"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M1.55556 12.4444H2.66389L10.2667 4.84167L9.15833 3.73333L1.55556 11.3361V12.4444ZM0 14V10.6944L10.2667 0.447222C10.4222 0.30463 10.594 0.194444 10.7819 0.116667C10.9699 0.0388889 11.1676 0 11.375 0C11.5824 0 11.7833 0.0388889 11.9778 0.116667C12.1722 0.194444 12.3407 0.311111 12.4833 0.466667L13.5528 1.55556C13.7083 1.69815 13.8218 1.86667 13.8931 2.06111C13.9644 2.25556 14 2.45 14 2.64444C14 2.85185 13.9644 3.04954 13.8931 3.2375C13.8218 3.42546 13.7083 3.59722 13.5528 3.75278L3.30556 14H0ZM9.70278 4.29722L9.15833 3.73333L10.2667 4.84167L9.70278 4.29722Z"
                                                  fill="#4D545C"
                                                />
                                              </svg>
                                            </button>
                                            <button
                                              type="button"
                                              className="px-2 py-1 text-sm text-[#4B5563] bg-white rounded"
                                              onClick={() =>
                                                showConfirmationToast(
                                                  'Are you sure you want to duplicate product?',
                                                  'Duplicate Product',
                                                  'info',
                                                  () =>
                                                    handleDuplicateClick(
                                                      addedProduct,
                                                      productInfo,
                                                      set.setIndex,
                                                    ),
                                                  'Duplicate',
                                                  undefined,
                                                  'Cancel',
                                                )
                                              }
                                            >
                                              <svg
                                                width="12"
                                                height="14"
                                                viewBox="0 0 12 14"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M4.2 11.2C3.815 11.2 3.48542 11.0629 3.21125 10.7887C2.93708 10.5146 2.8 10.185 2.8 9.8V1.4C2.8 1.015 2.93708 0.685417 3.21125 0.41125C3.48542 0.137083 3.815 0 4.2 0H10.5C10.885 0 11.2146 0.137083 11.4888 0.41125C11.7629 0.685417 11.9 1.015 11.9 1.4V9.8C11.9 10.185 11.7629 10.5146 11.4888 10.7887C11.2146 11.0629 10.885 11.2 10.5 11.2H4.2ZM4.2 9.8H10.5V1.4H4.2V9.8ZM1.4 14C1.015 14 0.685417 13.8629 0.41125 13.5887C0.137083 13.3146 0 12.985 0 12.6V2.8H1.4V12.6H9.1V14H1.4Z"
                                                  fill="#4D545C"
                                                />
                                              </svg>
                                            </button>
                                            <button
                                              type="button"
                                              className="px-2 py-1 text-sm bg-white rounded"
                                              onClick={() =>
                                                showConfirmationToast(
                                                  'Are you sure you want to delete this product?',
                                                  'Delete Product',
                                                  'delete',
                                                  () =>
                                                    handleDeleteProduct(
                                                      index,
                                                      set.setIndex,
                                                      addedProduct.butcherProductId,
                                                    ),
                                                  'Delete',
                                                  undefined,
                                                  'Cancel',
                                                )
                                              }
                                            >
                                              <FontAwesomeIcon
                                                icon={faTrashCan}
                                                className="text-[#4D545C] rounded-md cursor-pointer hover:opacity-70"
                                              />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      <div className="p-1 mt-2">
                                        {/* Cuts of meat is hiddent until the intergration is done */}
                                        {/* <div>
                                          <span className="text-[#43474E]">
                                            Cuts of Meat / Instructions:
                                          </span>
                                          <span className="ml-2 font-semibold text-gray-800">
                                            {addedProduct?.instruction ||
                                              '50% of the remaining meat'}
                                          </span>
                                        </div> */}
                                        <div className="border border-b my-1"></div>
                                        <div className="flex gap-2 text-sm">
                                          <span className="text-[#43474E] shrink-0">Quantity:</span>
                                          <span className="font-semibold text-[#1C1B1F]">
                                            {typeof addedProduct?.quantity === 'number' ? addedProduct.quantity : addedProduct?.quantity}
                                            {productInfo.productType === 2 ? ' Qty' : ' Lbs'}
                                          </span>
                                        </div>
                                        {addedProduct?.fatPercentage && Number(addedProduct.fatPercentage) !== 0 && (
                                          <div className="flex gap-2 text-sm">
                                            <span className="text-[#43474E] shrink-0">Fat:</span>
                                            <span className="font-semibold text-[#1C1B1F]">{addedProduct.fatPercentage}%</span>
                                          </div>
                                        )}
                                        <div className="border border-b my-1"></div>
                                        {addedProduct?.addons &&
                                          addedProduct.addons.length > 0 && (
                                            <>
                                              <div className="text-[#43474E] shrink-0">
                                                <span>Add-Ons:</span>
                                              </div>
                                              <div className="font-semibold text-[#1C1B1F]">
                                                <span>
                                                  {addedProduct.addons
                                                    .map(
                                                      (addon: AddonsList) =>
                                                        addon.name,
                                                    )
                                                    .join(', ')}
                                                </span>
                                              </div>
                                              <div className="border border-b my-1"></div>
                                            </>
                                          )}
                                        {addedProduct?.packages?.length > 0 && (
                                          <div className="space-y-1 text-sm text-[#43474E]">
                                            <p className="font-semibold">
                                              Packaging:
                                            </p>
                                            {addedProduct.packages.map(
                                              (pkg, pkgIndex: number) => (
                                                <div key={pkgIndex}>
                                                  <span>
                                                    {[
                                                      (pkg.packageType ?? '').trim(),
                                                      pkg.packageSize > 0
                                                        ? `Size: ${pkg.packageSize}${pkg.unit &&
                                                          pkg.unit.trim() !== '-'
                                                          ? ` ${pkg.unit}`
                                                          : ''
                                                        }`
                                                        : '',
                                                      pkg.percentage
                                                        ? `Pkg Split: ${normalizePackagePercentage(pkg.percentage)}%`
                                                        : '',
                                                    ]
                                                      .map((part) =>
                                                        typeof part === 'string'
                                                          ? part.trim()
                                                          : part,
                                                      )
                                                      .filter(
                                                        (part) =>
                                                          Boolean(part) &&
                                                          part !== '-',
                                                      )
                                                      .join(' - ')}
                                                  </span>
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              },
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
        {/* Edit product modal */}
        {editProductModal && selectedProduct && (
          <EditProductModal
            product={selectedProduct}
            allAddons={addons}
            allPackages={masterPackages}
            onClose={() => {
              setEditProductModal(false);
              setSelectedProduct(null);
            }}
            onSave={handleModalSave}
            productTypeInfo={productType}
          />
        )}

        {copyProductModal && selectedProduct && (
          <CopyProductModal
            product={selectedProduct}
            allAddons={addons}
            allPackages={masterPackages}
            onClose={() => {
              setCopyProductModal(false);
              setSelectedProduct(null);
            }}
            onSave={(updatedProduct) => {
              if (targetSetIndex !== null) {
                handleDuplicateProduct(updatedProduct, targetSetIndex);
              }
              setCopyProductModal(false);
              setSelectedProduct(null);
              showToastMessage(
                responseTexts.PRODUCT_DUPLICATE_SUCCESS,
                'success',
              );
            }}
            productTypeInfo={productType}
          />
        )}
      </div>
    </>
  );
};

export default OrderSplitView;
